import { X as XIcon } from 'react-feather';

import { Box, Button, Divider, Grid, IconButton, Modal, Typography } from '@mui/material';
import { CCForm } from '~/modules/payment/components';
import { FormProvider, useForm } from 'react-hook-form';
import { useState } from 'react';
import {
  useCreateCCPaymentMutation,
  useGetPaymentMethodQuery,
  useUpdatePaymentMethodMutation,
} from '~/services/member';
import usePayrixIds from '~/modules/payment/hooks/usePayrixIds';
import { LeavingWithoutSavingPrompt } from '~/modules/@shared/components';
import { PaymentErrorModal, PaymentProcessingModal, PaymentSuccessModal } from '~/modules/payment/components/modals';
import usePaymentErrorModalStore from '~/modules/payment/components/modals/PaymentErrorModal/hooks/usePaymentErrorModalStore';
import { CreatePreCCPaymentPayload, UpdateCCPaymentPayload } from '~/services/types';

type Props = {
  handleModalClose: Function;
  addCCOpen: boolean;
  locationId: number;
  memberId: number | undefined;
};

export default function AddCcModal({
  handleModalClose: handleExternalModalClose,
  addCCOpen,
  locationId,
  memberId,
}: Props) {
  const { customerId } = usePayrixIds({ memberId });

  const methods = useForm();
  const { handleSubmit, trigger, reset } = methods;

  const [processingOpen, setProcessingOpen] = useState(false);
  const [confirmedOpen, setConfirmedOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  const setErrorMessage = usePaymentErrorModalStore((state: any) => state.setMsg);

  const handleModalClose = () => {
    reset();
    setConfirmedOpen(false);
    setProcessingOpen(false);
    setErrorOpen(false);
    handleExternalModalClose();
  };

  const handleModalLocalClose = () => {
    reset();
    setErrorOpen(false);
  };

  const [paymentSubmit, setPaymentSubmit] = useState<Function | null>(null);
  const [pmId, setPmId] = useState(0);

  const { mutateAsync: createCCPayment } = useCreateCCPaymentMutation({ locationId });
  const { mutateAsync: updatePayment } = useUpdatePaymentMethodMutation({ locationId });
  const { refetch: refetchPaymentMethod } = useGetPaymentMethodQuery({
    locationId,
    memberId,
    paymentMethodId: pmId,
  });

  const createPreCCRequest = (location_id: number, member_id: number, zipcode: string): CreatePreCCPaymentPayload => {
    return {
      expiration: '', //response.data[0].expiration,
      default: true,
      inactive: false,
      location_id,
      member_id,
      method: 2, // (usually 2 for cards)
      zipcode,
    };
  };
  const updateCCRequest = (
    ccResponse: any,
    location_id: number,
    member_id: number,
    payment_method_id: number,
  ): UpdateCCPaymentPayload => {
    const responseToken =
      typeof ccResponse.response.data[0].token === 'string'
        ? ccResponse.response.data[0]
        : ccResponse.response.data[0].token;
    return {
      expiration: '', //response.data[0].expiration,
      last_4_digits_card: responseToken.payment.number,
      location_id,
      member_id,
      payrix_token_id: responseToken.id,
      token: responseToken.token,
      payment_method_id,
    };
  };
  const tryToRefreshPaymentMethod = (pmId: number) => {
    return new Promise((resolve, reject) => {
      let tryCount = 0;
      const intervalId = setInterval(() => {
        tryCount++;

        if (pmId && refetchPaymentMethod) {
          const rawResp = refetchPaymentMethod();
          rawResp
            .then((raw: any) => {
              const response = raw?.data ?? { payrix_zipcode_onboarding_status: -1 };
              if (response.payrix_zipcode_onboarding_status === 3) {
                clearInterval(intervalId);
                resolve(response);
              } else if (response.payrix_zipcode_onboarding_status !== 98) {
                console.error('rejected, incorrect payrix_zipcode_onboarding_status', { response });
                clearInterval(intervalId);
                reject(response);
              }
            })
            .catch((e: any) => {
              console.error('tryToRefreshPaymentMethod catched error', { e });
              //reject(e);
            });
        }
        if (tryCount === 10) {
          console.error('Max attempts reached', { tryCount });
          clearInterval(intervalId);
          reject({ payrix_zipcode_onboarding_status: -1 });
        }
      }, 1000);
    });
  };

  const handleSavePayment = async (data: any) => {
    setProcessingOpen(true);
    setErrorMessage('');
    try {
      if (paymentSubmit && customerId && data.zipcode) {
        let paymentMethodId = null;

        const prePaymentRequest = createPreCCRequest(locationId, memberId!, data.zipcode);
        const rawPreCcResponse = await createCCPayment(prePaymentRequest);

        if (rawPreCcResponse?.status === 'CREATED') {
          paymentMethodId = rawPreCcResponse.data.id;
          setPmId(paymentMethodId);
        } else {
          console.error('Error on payments CC: ', { prePaymentRequest, rawPreCcResponse });
          throw new Error('Error on payments CC: ' + (rawPreCcResponse?.message ?? 'Unknown error'));
        }

        const preCcResponse = rawPreCcResponse?.data ?? { payrix_zipcode_onboarding_status: -1 };

        if (preCcResponse.payrix_zipcode_onboarding_status === 98) {
          const newCcResponse: any = await tryToRefreshPaymentMethod(paymentMethodId);
          if (newCcResponse?.payrix_zipcode_onboarding_status !== 3) {
            throw new Error('Zipcode was not onboarded');
          }
        } else {
          throw new Error('Zipcode was not accepted');
        }

        const ccResponse = await paymentSubmit(customerId);

        const paymentRequest = updateCCRequest(ccResponse, locationId, memberId!, paymentMethodId);
        const updateCCPaymentResponse = await updatePayment({
          request: paymentRequest,
          paymentMethodId,
        });
        if (updateCCPaymentResponse?.status === 'UPDATED') {
          setProcessingOpen(false);
          setConfirmedOpen(true);
        } else {
          console.error('Error on payments CC: ', { paymentRequest, updateCCPaymentResponse });
          throw new Error('Error on payments CC: ' + (updateCCPaymentResponse?.message ?? 'Unknown error'));
        }
      } else {
        console.error('Error on CC payments', { paymentSubmit, customerId });
        throw new Error('Error on payments');
      }
    } catch (e: any) {
      console.error({ e });
      setErrorMessage(e.message);
      setProcessingOpen(false);
      setErrorOpen(true);
    }
  };

  const handleFormError = () => {};

  return (
    <>
      <FormProvider {...methods}>
        <LeavingWithoutSavingPrompt />
        <Modal
          open={addCCOpen}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box
            sx={{
              borderRadius: '10px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              //maxHeight: 300,
              bgcolor: 'rgb(246, 247, 251)',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Typography variant="h2">Add Credit/Debit Card</Typography>
              <IconButton onClick={handleModalClose}>
                <XIcon size={16} />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ px: 2 }}>
              <Grid container spacing={2}>
                <CCForm {...{ setPaymentSubmit, locationId, memberId }} />
              </Grid>
            </Box>
            <Divider />
            <Box sx={{ pt: 3, pb: 3, pr: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Button variant="outlined" size="large" onClick={handleModalClose} sx={{ marginRight: '1em' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  void trigger();
                  void handleSubmit(handleSavePayment, handleFormError)();
                }}
                sx={{ width: '200px' }}>
                Save Payment
              </Button>
            </Box>
          </Box>
        </Modal>
      </FormProvider>
      <PaymentProcessingModal {...{ processingOpen }} />
      <PaymentSuccessModal {...{ confirmedOpen, handleModalClose }} />
      <PaymentErrorModal {...{ errorOpen, handleModalClose, handleModalLocalClose }} />
    </>
  );
}
